// 단가표 원가·마진 한 벌 — 적용 마진(품목 → 공종 → 회사)과 원가에서 판매 단가를 낸다 · 설정 화면·단가 제안·저장이 같이 쓴다.

import { MoneyRangeError } from "../money/checked";
import type { VatMode } from "../money/vat";
import { convertUnitPrice, saleFromMargin } from "../quote/margin";

// 마진 만분율 범위(-90% ~ +1000%) — 0099 check 와 같은 자.
export const MARGIN_BP_MIN = -9000;
export const MARGIN_BP_MAX = 100_000;
// 단가표 단가·원가 한 칸의 상한 — 저장 검사(zod)와 화면 입력이 같은 자를 쓴다.
export const PRICE_INPUT_MAX = 9_999_999_999;
// 회사 기본 마진이 비었거나 읽을 수 없을 때 — +20%.
export const DEFAULT_QUOTE_MARGIN_BP = 2000;

export interface MarginSettings {
  default_bp: number;
  trades: Record<string, number>;
}

export interface PriceCostFields {
  trade: string;
  material_unit_price: number;
  labor_unit_price: number;
  material_cost?: number | null;
  labor_cost?: number | null;
  margin_bp?: number | null;
}

export interface PricedSale {
  material_unit_price: number;
  labor_unit_price: number;
  cost_unit_price: number | null;
  base_margin_bp: number | null;
}

export const isMarginBp = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= MARGIN_BP_MIN &&
  value <= MARGIN_BP_MAX;

// 설정 두 칸(quote_margin_bp·quote_trade_margins)을 읽는다 — 읽을 수 없는 값은 버리고 기본으로 떨어진다.
export const parseMarginSettings = (
  defaultRaw: string | null | undefined,
  tradesRaw: string | null | undefined
): MarginSettings => {
  const raw = (defaultRaw ?? "").trim();
  const parsed = raw === "" ? Number.NaN : Number(raw);
  const trades: Record<string, number> = {};
  try {
    const obj: unknown = JSON.parse(tradesRaw ?? "{}");
    if (obj && typeof obj === "object" && !Array.isArray(obj)) {
      for (const [code, bp] of Object.entries(obj as Record<string, unknown>)) {
        if (code.trim() !== "" && isMarginBp(bp)) trades[code] = bp;
      }
    }
  } catch {
    // 깨진 JSON 은 공종 마진 없음으로 본다.
  }
  return { default_bp: isMarginBp(parsed) ? parsed : DEFAULT_QUOTE_MARGIN_BP, trades };
};

// 적용 마진 = 품목 마진 → 공종 마진 → 회사 기본.
export const appliedMarginBp = (
  price: Pick<PriceCostFields, "trade" | "margin_bp">,
  settings: MarginSettings
): number => {
  if (isMarginBp(price.margin_bp)) return price.margin_bp;
  const trade = settings.trades[price.trade];
  return isMarginBp(trade) ? trade : settings.default_bp;
};

// 원가 합(자재+인건) — 둘 다 비었거나 합이 0 이면 원가 없음(null).
export const costTotalOf = (
  price: Pick<PriceCostFields, "material_cost" | "labor_cost">
): number | null => {
  const m = price.material_cost ?? null;
  const l = price.labor_cost ?? null;
  if (m === null && l === null) return null;
  const total = (m ?? 0) + (l ?? 0);
  return total > 0 ? total : null;
};

// 판매 합계를 원가 비율로 자재·인건에 나눈다 — 자재는 내림, 나머지는 인건이라 합이 정확하다 · 비율이 한쪽뿐이면 그쪽에 전부.
export const splitSale = (
  total: number,
  materialWeight: number,
  laborWeight: number
): { material: number; labor: number } => {
  const mw = Math.max(0, materialWeight);
  const lw = Math.max(0, laborWeight);
  if (mw === 0 && lw === 0) return { material: total, labor: 0 };
  if (lw === 0) return { material: total, labor: 0 };
  if (mw === 0) return { material: 0, labor: total };
  const sum = mw + lw;
  const product = total * mw;
  const raw = Number.isSafeInteger(product)
    ? Math.floor(product / sum)
    : Math.floor(total * (mw / sum));
  const material = Math.min(total, Math.max(0, raw));
  return { material, labor: total - material };
};

// 단가표 한 줄의 판매 단가 — 원가가 있으면 원가 × (1 + 적용 마진)을 그 견적 방식으로, 없으면 저장된 판매가(공급가)를 바꿔 쓴다.
export const pricedSale = (
  price: PriceCostFields,
  settings: MarginSettings,
  vatMode: VatMode = "EXCL"
): PricedSale => {
  const cost = costTotalOf(price);
  if (cost === null) {
    return {
      material_unit_price: convertUnitPrice(price.material_unit_price, "EXCL", vatMode),
      labor_unit_price: convertUnitPrice(price.labor_unit_price, "EXCL", vatMode),
      cost_unit_price: null,
      base_margin_bp: null,
    };
  }
  const bp = appliedMarginBp(price, settings);
  const total = saleFromMargin(cost, bp / 10_000, vatMode);
  const { material, labor } = splitSale(total, price.material_cost ?? 0, price.labor_cost ?? 0);
  return { material_unit_price: material, labor_unit_price: labor, cost_unit_price: cost, base_margin_bp: bp };
};

// 원가 판매가가 금액 범위를 넘으면 그 줄만 저장된 단가로 — 단가표 loader·화면이 같이 쓰고 다른 오류는 그대로 던진다.
export const pricedSaleOrStored = (
  price: PriceCostFields,
  settings: MarginSettings,
  vatMode: VatMode = "EXCL"
): PricedSale => {
  try {
    return pricedSale(price, settings, vatMode);
  } catch (error) {
    if (!(error instanceof MoneyRangeError)) throw error;
    return pricedSale({ ...price, material_cost: null, labor_cost: null }, settings, vatMode);
  }
};

// 원가 × 마진 판매가(공급가)가 단가 한 칸 상한을 넘나 — 저장 검사·화면 입력이 같이 쓴다 · 원가 없는 줄은 false.
export const saleOverMax = (price: PriceCostFields, settings: MarginSettings): boolean => {
  if (costTotalOf(price) === null) return false;
  try {
    const sale = pricedSale(price, settings, "EXCL");
    return sale.material_unit_price > PRICE_INPUT_MAX || sale.labor_unit_price > PRICE_INPUT_MAX;
  } catch (error) {
    if (error instanceof MoneyRangeError) return true;
    throw error;
  }
};

// 상한을 넘는 줄을 막을 때의 문구 — 뒤에 「 — 항목」 을 붙인다.
export const SALE_OVER_MAX_MESSAGE = "원가 × 마진 판매가가 단가 상한(9,999,999,999원)을 넘습니다";

// 이익률 = 마진 ÷ (1 + 마진) — +20% → 16.7% · 마진 -100% 이하는 null.
export const profitRateOf = (bp: number): number | null => {
  const m = bp / 10_000;
  return 1 + m > 0 ? m / (1 + m) : null;
};
